import { supabase } from '../utils/supabase';
import { combinedEloFromPcrRow } from '../utils/combinedLeaderboardElo';
import {
  assignLeaderboardRanks,
  buildLeaderboardPreview,
  filterLeaderboardByScope,
  LEADERBOARD_SCOPES,
} from '../utils/leaderboardPlayerScope';
import {
  collectPlayerIdsFromRecentSnapshots,
  fetchRecentSnapshotsForScope,
} from './leaderboardMovementService';
import { buildMovementMapForScope } from '../utils/leaderboardSnapshotUtils';

export { LEADERBOARD_SCOPES };

const isNotArchivedGuest = (player) => {
  if (!player) return false;
  return !(player.is_guest === true && player.is_active === false);
};

async function fetchActiveMembershipPlayerIds(clubId) {
  const { data, error } = await supabase
    .from('players_club')
    .select('player_id')
    .eq('club_id', clubId)
    .eq('is_active', true);
  if (error) throw error;
  return new Set((data || []).map((row) => row.player_id));
}

const PCR_PLAYER_SELECT = `
  id,
  first_name,
  last_name,
  gender,
  avatar_url,
  is_guest,
  is_active
`;

/**
 * Maps RPC rows into the session summary shape used by leaderboard filtering.
 *
 * @param {Array<{ player_id: string, sessions_played: number, last_session_date: string|null }>} rows
 * @param {string[]} playerIds
 */
export function mapActivityRowsToSummaries(rows, playerIds) {
  const byPlayer = {};
  for (const row of rows ?? []) {
    if (!row.player_id) continue;
    byPlayer[row.player_id] = {
      sessionsPlayed: row.sessions_played ?? 0,
      lastSessionDate: row.last_session_date ?? null,
      sessionDates: row.last_session_date ? [row.last_session_date] : [],
    };
  }

  const result = {};
  for (const id of playerIds) {
    result[id] = byPlayer[id] ?? {
      sessionsPlayed: 0,
      lastSessionDate: null,
      sessionDates: [],
    };
  }
  return result;
}

/**
 * Legacy paginated fetch — fallback when RPC is not yet deployed.
 */
async function fetchClubLeaderboardSessionSummariesPaginated(playerIds, seasonId) {
  const ids = [...new Set((playerIds || []).filter(Boolean))];
  if (ids.length === 0 || !seasonId) return {};

  const pageSize = 1000;
  let offset = 0;
  const data = [];

  while (true) {
    const { data: page, error } = await supabase
      .from('players_session_games')
      .select(`
        player_id,
        session_games!inner (
          session_id,
          sessions!inner (
            id,
            season_id,
            session_date
          )
        )
      `)
      .in('player_id', ids)
      .eq('session_games.sessions.season_id', seasonId)
      .range(offset, offset + pageSize - 1);

    if (error) {
      console.error('[fetchClubLeaderboardSessionSummariesPaginated]', error);
      throw error;
    }

    if (page?.length) {
      data.push(...page);
    }
    if (!page || page.length < pageSize) break;
    offset += pageSize;
  }

  const byPlayer = {};
  for (const row of data) {
    const playerId = row.player_id;
    const sessionDate = row.session_games?.sessions?.session_date;
    if (!playerId) continue;
    if (!byPlayer[playerId]) {
      byPlayer[playerId] = { sessionIds: new Set(), sessionDates: [] };
    }
    const sessionId = row.session_games?.sessions?.id;
    if (sessionId) {
      byPlayer[playerId].sessionIds.add(sessionId);
    }
    if (sessionDate) {
      byPlayer[playerId].sessionDates.push(sessionDate);
    }
  }

  const result = {};
  for (const id of ids) {
    const entry = byPlayer[id];
    const sessionDates = entry?.sessionDates ?? [];
    const lastSessionDate =
      sessionDates.length > 0
        ? sessionDates.reduce((latest, d) => (d > latest ? d : latest), sessionDates[0])
        : null;
    result[id] = {
      sessionsPlayed: entry ? entry.sessionIds.size : 0,
      lastSessionDate,
      sessionDates: [...new Set(sessionDates)].sort(),
    };
  }
  return result;
}

/**
 * Fetches per-player session counts and last session date (by sessions.session_date).
 * Prefers get_club_season_player_activity RPC; falls back to paginated client aggregate.
 *
 * @returns {Promise<Record<string, { sessionsPlayed: number, lastSessionDate: string|null, sessionDates: string[] }>>}
 */
export async function fetchClubLeaderboardSessionSummaries(playerIds, seasonId, clubId) {
  const ids = [...new Set((playerIds || []).filter(Boolean))];
  if (ids.length === 0 || !seasonId) return {};

  if (clubId) {
    const { data, error } = await supabase.rpc('get_club_season_player_activity', {
      p_club_id: clubId,
      p_season_id: seasonId,
    });

    if (!error && data) {
      return mapActivityRowsToSummaries(data, ids);
    }

    if (error) {
      console.warn('[fetchClubLeaderboardSessionSummaries] RPC unavailable, using paginated fallback', error.message);
    }
  }

  return fetchClubLeaderboardSessionSummariesPaginated(ids, seasonId);
}

/**
 * Fetches distinct session dates for a subset of players (Active movement only).
 *
 * @returns {Promise<Record<string, string[]>>}
 */
export async function fetchPlayerSessionDatesForIds(playerIds, clubId, seasonId) {
  const ids = [...new Set((playerIds || []).filter(Boolean))];
  if (ids.length === 0 || !clubId || !seasonId) return {};

  const pageSize = 1000;
  let offset = 0;
  const data = [];

  while (true) {
    const { data: page, error } = await supabase
      .from('players_session_games')
      .select(`
        player_id,
        session_games!inner (
          club_id,
          sessions!inner (
            season_id,
            session_date
          )
        )
      `)
      .in('player_id', ids)
      .eq('session_games.club_id', clubId)
      .eq('session_games.sessions.season_id', seasonId)
      .range(offset, offset + pageSize - 1);

    if (error) {
      console.error('[fetchPlayerSessionDatesForIds]', error);
      throw error;
    }

    if (page?.length) {
      data.push(...page);
    }
    if (!page || page.length < pageSize) break;
    offset += pageSize;
  }

  const byPlayer = {};
  for (const row of data) {
    const playerId = row.player_id;
    const sessionDate = row.session_games?.sessions?.session_date;
    if (!playerId || !sessionDate) continue;
    if (!byPlayer[playerId]) {
      byPlayer[playerId] = new Set();
    }
    byPlayer[playerId].add(sessionDate);
  }

  const result = {};
  for (const id of ids) {
    result[id] = byPlayer[id] ? [...byPlayer[id]].sort() : [];
  }
  return result;
}

/**
 * @returns {Record<string, string[]>}
 */
export function sessionDatesMapFromSummaries(summaries) {
  const map = {};
  Object.entries(summaries || {}).forEach(([playerId, summary]) => {
    map[playerId] = summary.sessionDates ?? [];
  });
  return map;
}

function enrichWithSessionSummary(item, summaries) {
  const playerId = item.players?.id;
  const summary = summaries[playerId] ?? {};
  return {
    ...item,
    sessionsPlayed: summary.sessionsPlayed ?? 0,
    lastSessionDate: summary.lastSessionDate ?? null,
  };
}

/**
 * Processes raw PCR rows into a scoped, ranked leaderboard list.
 *
 * @param {Object} params
 * @param {Array} params.rawRows
 * @param {'active'|'all'} params.scope
 * @param {Record<string, Object>} params.sessionSummaries
 * @param {(item: Object) => number|null} params.getElo
 * @param {(item: Object) => string|undefined} params.getPlayerId
 * @param {(item: Object) => boolean} [params.extraFilter]
 */
export function processScopedLeaderboard({
  rawRows,
  scope,
  sessionSummaries,
  getElo,
  getPlayerId,
  extraFilter = () => true,
}) {
  const withSessions = (rawRows ?? [])
    .filter((item) => isNotArchivedGuest(item.players))
    .filter(extraFilter)
    .map((item) => enrichWithSessionSummary(item, sessionSummaries));

  const scoped = filterLeaderboardByScope(withSessions, scope, sessionSummaries);
  const ranked = assignLeaderboardRanks(scoped, getElo, getPlayerId);

  return ranked.map((item) => ({
    ...item,
    eloRating: getElo(item),
  }));
}

/**
 * Fetches all ladder data for ClubPage (mixed, levels male/female, combined).
 *
 * @param {Object} params
 * @param {string} params.clubId
 * @param {string} params.seasonId
 * @param {'active'|'all'} params.scope
 * @param {string|null|undefined} params.currentUserId
 */
export async function fetchClubLeaderboardsBundle({ clubId, seasonId, scope, currentUserId }) {
  const { data: pcrData, error: pcrError } = await supabase
    .from('player_club_elo_ratings')
    .select(`
      mixed_elo_rating,
      levels_elo_rating,
      mixed_games_played,
      levels_games_played,
      player_id,
      players:player_id (${PCR_PLAYER_SELECT})
    `)
    .eq('club_id', clubId)
    .eq('season_id', seasonId);

  if (pcrError) throw pcrError;

  const activeMemberIds = await fetchActiveMembershipPlayerIds(clubId);
  const rows = (pcrData ?? []).filter((row) => activeMemberIds.has(row.player_id));
  const playerIds = [...new Set(
    [...rows.map((r) => r.players?.id ?? r.player_id), currentUserId].filter(Boolean)
  )];
  const sessionSummaries = await fetchClubLeaderboardSessionSummaries(playerIds, seasonId, clubId);

  const levelsMaleRanked = processScopedLeaderboard({
    rawRows: rows.filter((r) => r.levels_elo_rating != null),
    scope,
    sessionSummaries,
    getElo: (item) => item.levels_elo_rating,
    getPlayerId: (item) => item.players?.id,
    extraFilter: (item) => item.players?.gender === 'male',
  });

  const levelsFemaleRanked = processScopedLeaderboard({
    rawRows: rows.filter((r) => r.levels_elo_rating != null),
    scope,
    sessionSummaries,
    getElo: (item) => item.levels_elo_rating,
    getPlayerId: (item) => item.players?.id,
    extraFilter: (item) => item.players?.gender === 'female',
  });

  const mixedRanked = processScopedLeaderboard({
    rawRows: rows.filter((r) => r.mixed_elo_rating != null),
    scope,
    sessionSummaries,
    getElo: (item) => item.mixed_elo_rating,
    getPlayerId: (item) => item.players?.id,
  });

  const combinedCandidates = rows
    .filter((item) => isNotArchivedGuest(item.players))
    .map((item) => {
      const combinedElo = combinedEloFromPcrRow(item);
      if (combinedElo == null) return null;
      return enrichWithSessionSummary({ ...item, combined_elo_rating: combinedElo }, sessionSummaries);
    })
    .filter(Boolean);

  const combinedRanked = assignLeaderboardRanks(
    filterLeaderboardByScope(combinedCandidates, scope, sessionSummaries),
    (item) => item.combined_elo_rating,
    (item) => item.players?.id
  ).map((item) => ({ ...item, eloRating: item.combined_elo_rating }));

  const malePreview = buildLeaderboardPreview(levelsMaleRanked, currentUserId, 5);
  const femalePreview = buildLeaderboardPreview(levelsFemaleRanked, currentUserId, 5);
  const mixedPreview = buildLeaderboardPreview(mixedRanked, currentUserId, 10);
  const combinedPreview = buildLeaderboardPreview(combinedRanked, currentUserId, 10);

  return {
    sessionSummaries,
    sessionDatesByPlayerId: sessionDatesMapFromSummaries(sessionSummaries),
    leaderboard: {
      levelsMale: malePreview.preview,
      levelsFemale: femalePreview.preview,
      mixed: mixedPreview.preview,
      combined: combinedPreview.preview,
      userPositions: {
        levelsMale: malePreview.userRank,
        levelsFemale: femalePreview.userRank,
        mixed: mixedPreview.userRank,
        combined: combinedPreview.userRank,
      },
      fullRanked: {
        levelsMale: levelsMaleRanked,
        levelsFemale: levelsFemaleRanked,
        mixed: mixedRanked,
        combined: combinedRanked,
      },
    },
  };
}

/**
 * Fetches a single ladder for FullLeaderboardPage.
 *
 * @param {Object} params
 * @param {string} params.clubId
 * @param {string} params.seasonId
 * @param {'mixed'|'levels-male'|'levels-female'|'combined'} params.gameType
 * @param {'active'|'all'} params.scope
 */
export async function fetchSingleClubLeaderboard({ clubId, seasonId, gameType, scope }) {
  const { data: pcrData, error: pcrError } = await supabase
    .from('player_club_elo_ratings')
    .select(`
      mixed_elo_rating,
      levels_elo_rating,
      mixed_games_played,
      levels_games_played,
      player_id,
      players:player_id (${PCR_PLAYER_SELECT})
    `)
    .eq('club_id', clubId)
    .eq('season_id', seasonId);

  if (pcrError) throw pcrError;

  const activeMemberIds = await fetchActiveMembershipPlayerIds(clubId);
  const rows = (pcrData ?? []).filter((row) => activeMemberIds.has(row.player_id));
  const playerIds = rows.map((r) => r.players?.id ?? r.player_id).filter(Boolean);
  const sessionSummaries = await fetchClubLeaderboardSessionSummaries(playerIds, seasonId, clubId);

  let ranked;
  if (gameType === 'combined') {
    const combinedCandidates = rows
      .filter((item) => isNotArchivedGuest(item.players))
      .map((item) => {
        const combinedElo = combinedEloFromPcrRow(item);
        if (combinedElo == null) return null;
        return enrichWithSessionSummary(
          { ...item, combined_elo_rating: combinedElo },
          sessionSummaries
        );
      })
      .filter(Boolean);
    ranked = assignLeaderboardRanks(
      filterLeaderboardByScope(combinedCandidates, scope, sessionSummaries),
      (item) => item.combined_elo_rating,
      (item) => item.players?.id
    ).map((item) => ({ ...item, eloRating: item.combined_elo_rating }));
  } else {
    const config = {
      mixed: {
        eloField: 'mixed_elo_rating',
        extraFilter: () => true,
      },
      'levels-male': {
        eloField: 'levels_elo_rating',
        extraFilter: (item) => item.players?.gender === 'male',
      },
      'levels-female': {
        eloField: 'levels_elo_rating',
        extraFilter: (item) => item.players?.gender === 'female',
      },
    }[gameType];

    if (!config) {
      throw new Error(`Invalid game type: ${gameType}`);
    }

    ranked = processScopedLeaderboard({
      rawRows: rows.filter((r) => r[config.eloField] != null),
      scope,
      sessionSummaries,
      getElo: (item) => item[config.eloField],
      getPlayerId: (item) => item.players?.id,
      extraFilter: config.extraFilter,
    });
  }

  const totalPlayers = ranked.length;
  const totalElo = ranked.reduce((sum, item) => sum + (item.eloRating || 0), 0);
  const averageElo = totalPlayers > 0 ? Math.round(totalElo / totalPlayers) : 0;

  return {
    leaderboard: ranked,
    sessionSummaries,
    sessionDatesByPlayerId: sessionDatesMapFromSummaries(sessionSummaries),
    kpis: { totalPlayers, averageElo },
  };
}

function levelsLadderKeyForGender(viewerGender) {
  if (viewerGender === 'male') return { ladderType: 'levels_male', positionKey: 'levelsMale' };
  if (viewerGender === 'female') return { ladderType: 'levels_female', positionKey: 'levelsFemale' };
  return null;
}

function teaserEntryFromRankedBoard({ rank, rankedRows, viewerPlayerId, movementMap }) {
  if (rank == null) return null;
  const row = (rankedRows || []).find((item) => item.players?.id === viewerPlayerId);
  const movement = movementMap?.get(viewerPlayerId);
  return {
    rank,
    rank_delta: movement?.rank_delta ?? null,
    elo_rating: row?.eloRating ?? null,
    elo_delta: movement?.elo_delta ?? null,
  };
}

/**
 * Viewer's Mixed + levels ranks from a live scoped bundle (same numbers as ClubPage).
 * Movement maps are optional; missing chips stay null (RankMovementLabel shows New).
 */
export function viewerLadderTeaserFromBundle({
  clubId,
  seasonId,
  viewerPlayerId,
  viewerGender,
  userPositions,
  fullRanked,
  movementByLadder = {},
}) {
  const levelsMeta = levelsLadderKeyForGender(viewerGender);
  return {
    clubId,
    seasonId,
    levelsLadder: levelsMeta?.ladderType ?? null,
    mixed: teaserEntryFromRankedBoard({
      rank: userPositions?.mixed ?? null,
      rankedRows: fullRanked?.mixed,
      viewerPlayerId,
      movementMap: movementByLadder.mixed,
    }),
    levels: levelsMeta
      ? teaserEntryFromRankedBoard({
          rank: userPositions?.[levelsMeta.positionKey] ?? null,
          rankedRows: fullRanked?.[levelsMeta.positionKey],
          viewerPlayerId,
          movementMap: movementByLadder[levelsMeta.positionKey],
        })
      : null,
  };
}

/**
 * Live Active-scope teaser for home club cards — PCR ranks + Active movement chips.
 */
export async function fetchViewerActiveLadderTeaser({
  clubId,
  seasonId,
  viewerPlayerId,
  viewerGender,
}) {
  const bundle = await fetchClubLeaderboardsBundle({
    clubId,
    seasonId,
    scope: LEADERBOARD_SCOPES.ACTIVE,
    currentUserId: viewerPlayerId,
  });

  const ladders = [{ key: 'mixed', ladderType: 'mixed' }];
  const levelsMeta = levelsLadderKeyForGender(viewerGender);
  if (levelsMeta) {
    ladders.push({ key: levelsMeta.positionKey, ladderType: levelsMeta.ladderType });
  }

  const recentSnapshotsList = await Promise.all(
    ladders.map(({ ladderType }) =>
      fetchRecentSnapshotsForScope(clubId, seasonId, ladderType, LEADERBOARD_SCOPES.ACTIVE).catch(
        () => null
      )
    )
  );

  let sessionDatesByPlayerId = {};
  const snapshotPlayerIds = collectPlayerIdsFromRecentSnapshots(recentSnapshotsList);
  if (snapshotPlayerIds.length > 0) {
    sessionDatesByPlayerId = await fetchPlayerSessionDatesForIds(
      snapshotPlayerIds,
      clubId,
      seasonId
    );
  }

  const movementByLadder = {};
  ladders.forEach(({ key }, index) => {
    movementByLadder[key] = buildMovementMapForScope(
      LEADERBOARD_SCOPES.ACTIVE,
      recentSnapshotsList[index],
      sessionDatesByPlayerId
    );
  });

  return viewerLadderTeaserFromBundle({
    clubId,
    seasonId,
    viewerPlayerId,
    viewerGender,
    userPositions: bundle.leaderboard.userPositions,
    fullRanked: bundle.leaderboard.fullRanked,
    movementByLadder,
  });
}
